import isEmpty from 'is-empty';
import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import ReactPixel from 'react-facebook-pixel';
import toast from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import {
    redirectPaymentGatewayForCartMutation, saveCartMutation, updateCartPaymentMethodMutation,
    updateOccupantMutation
} from 'rsl-api-106/lib/mutations';
import {
    getBookingProductsQuery, getCartQuery, hasProductsForOccupantQuery
} from 'rsl-api-106/lib/queries';
import {
    BookingStatus, CartPaymentMethodInput, CustomerBooking, Maybe, Mutation,
    MutationRedirectPaymentGatewayForCartArgs, MutationSaveCartArgs,
    MutationUpdateCartPaymentMethodArgs, MutationUpdateOccupantArgs, Product, ProductByCategory,
    ProductDistributionType, Query, QueryGetAccommodationProductsArgs, QueryGetBookingProductsArgs,
    QueryGetCartArgs, QueryHasProductsForOccupantArgs
} from 'rsl-api-106/lib/types';

import { useApolloClient } from '@apollo/client';
import Alert from '@components/_Layout/Alert';
import Button from '@components/_Layout/Controls/Button';
import LinkButton from '@components/_Layout/Controls/LinkButton';
import Popup from '@components/_Layout/Controls/Popup';
import LoadingScreen from '@components/_Layout/Loaders/LoadingScreen';
import ProductList from '@components/Products/ProductList';
import BasketSummary from '@components/Reservation/BasketSummary';
import { PopupEdit } from '@components/Reservation/BasketSummary/BasketSummary';
import { StepTitle } from '@components/Reservation/Steps';
import StepAuth from '@components/Reservation/Steps/StepAuth';
import StepFastcheckout from '@components/Reservation/Steps/StepFastcheckout';
import StepPayment from '@components/Reservation/Steps/StepPayment';
import StepReservationContent from '@components/Reservation/Steps/StepReservationContent';
import {
    GTMReservationPageStep1, GTMReservationPageStep2, GTMReservationPageStep3,
    GTMReservationPageStep4
} from '@components/Tracking/GTMReservationPage';
import { useAuth, useCart, useConfig, useLoadingScreen, useScrollTop, useSession } from '@hooks';
import { ArrowCircleLeft, ArrowCircleRight } from '@mui/icons-material';
import { Step, StepLabel, Stepper } from '@mui/material';
import {
    getAccommodationProductsForStepsQuery, hasProductsForOccupantWithoutTaxesQuery,
    QueryHasProductsForOccupantWithoutTaxesArgs
} from '@services/graphql2/querymutations';
import { getCurrencySymbol } from '@utils/currencies';
import dateFormat from '@utils/dateformat';
import { focusElementById, getAbsoluteImagePath, scrollTop } from '@utils/dom';
import {
    getLocalStorageItem, removeLocalStorageItem, setLocalStorageItem
} from '@utils/localStorage';
import { getCustomerUrlByWebUser } from '@utils/tools';
import { trackClick } from '@utils/tracking/gtm';

import { TAXES_PRODUCTS } from '../constants';
import { PspCallbackStatus, TErrorsSteps } from '../types';

type ReservationPageProps = {
  //
};
export enum StepsEnum {
  AUTH = 0,
  ACTIVITIES = 1,
  OCCUPANT = 2,
  PAYMENT = 3,
}
const languagesVarsCodes = [
  "LANG_neobooking_step_reservation", //Réservation
  "LANG_neobooking_step_auth", //Authentification
  "LANG_neobooking_step_payment", //Paiement
  "LANG_neobooking_step_presta", //Préstation
  "LANG_neobooking_back", //Retour
  "LANG_neobooking_back_previous_step", //Revenir à l'étape précédente
  "LANG_neobooking_next", //Suivant
  "LANG_neobooking_psp_success", //Votre paiement a été validée.
  "LANG_neobooking_psp_decline", //Votre paiement a été décliné.
  "LANG_neobooking_psp_cancel", //Votre paiement a été annulé.
  "LANG_neobooking_psp_exception", //Une erreur est survenue lors de votre paiement.
  "LANG_neobooking_complete_all_occupants_details", //Veuillez renseigner et enregistrer les informations de tout les occupants.
  "LANG_neobooking_auth_required_to_continue", //Vous devez vous connecter afin de poursuivre votre réservation.
  "LANG_neobooking_payment_due_required", //Vous devez choisir un type d'échéance.
  "LANG_neobooking_please_wait", //Veuillez patienter quelques instants
  "LANG_neobooking_cart_empty", //Votre panier est vide
  "LANG_neobooking_cart_empty_description", //Ajoutez un séjour pour poursuivre votre réservation.
  "LANG_neobooking_to_date", //Au
  "LANG_neobooking_nights", //Nuits
  "LANG_neobooking_insurance_holiday", //ASSUREZ VOS VACANCES
  "LANG_neobooking_select_insurance", //Veuillez sélectionner 1 option d'assurance.
  "LANG_neobooking_info_insurance", //Pour rappel, les assurances comprennent
  "LANG_neobooking_insurance_cancellation_stay", //Annulation et interruption de séjour (décès, maladie, hospitalisation, dommages grave de votre véhicule
  "LANG_neobooking_insurance_changes_vacation_dates", //Suppression ou modification des dates de congés pays du fait de l'employeur
  "LANG_neobooking_insurance_job_transfer", //Mutation professionnelle, obtention d'un emploi
  "LANG_neobooking_insurance_covid", //COVID-19 (cas contact, contamination, maladie)
  "LANG_neobooking_make_my_choice", //Je fais mon choix
  "LANG_neobooking_continue_without_insurance", //Continuer sans assurance
  "LANG_neobooking_no_dispo_message", //L'hébergement sélectionné n'est plus disponible. Merci d'effectuer une nouvelle recherche.
  "LANG_neobooking_new_search", //Nouvelle recherche
];
/**
 * Given a reservation, return the name of the rooms that are part of the reservation
 * @param {CustomerBooking} reservation - Reservation
 * @returns The name of the rooms that are being reserved.
 */
const getReservationRoomsInfosName = (reservation: CustomerBooking) => {
  let title = "";
  reservation?.stays?.forEach((stay, stayIndex) => {
    return (
      <div>
        {" "}
        {stay?.distribution?.roomTypes?.forEach((roomInfo, roomInfoIndex) => {
          title =
            title +
            `${stayIndex === 0 ? "" : "+ "} ${roomInfo?.quantity}x ${
              roomInfo?.label ?? ""
            } `;
        })}
      </div>
    );
  });
  return title;
};
type ResaStep = {
  label: string;
  show: boolean;
};
const ReservationPage: React.FC<ReservationPageProps> = () => {
  /* Hooks */
  //on scroll top automatiquement en haut de page au chargement du composant
  useScrollTop();
  const client = useApolloClient();
  const { sessionId, getLanguageVarValue, lang, authMode } = useSession();
  const { user, loading: userLoading } = useAuth();
  const {
    cart,
    save,
    addProposalFromKeys,
    loading: loadingCart,
    confirmFastCheckout,
  } = useCart();
  const [searchParams, setSearchParams] = useSearchParams();

  const { mode: clientMode, logo: clientLogo } = useConfig();
  const mode = clientMode ? clientMode : "vacanceole";
  const logo = clientLogo ? clientLogo : "Vacanceole-B-RVB.png";

  //steps state
  const stepParam: string | null = searchParams.get("step");
  const [activeStep, setActiveStep] = useState<number>(
    stepParam ? parseInt(stepParam) : authMode === 1 ? 1 : 0
  ); //step param First, else localStorage step value, else 0
  const [errorsSteps, setErrorsSteps] = useState<TErrorsSteps[]>([]);
  const [activeErrorsSteps, setActiveErrorsSteps] =
    useState<TErrorsSteps | undefined>(undefined);

  //terms state
  const [termsChecked, setTermsChecked] = useState<boolean>(false);
  const [termsError, setTermsError] = useState<boolean>(false);
  //payment state
  const [selectedPaymentDueType, setSelectedPaymentDueType] =
    useState<string | undefined>(undefined);
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<string | undefined>(undefined);
  const [selectedPaymentOption, setSelectedPaymentOption] =
    useState<string | undefined>(undefined);
  //insurance state
  const [selectedInsurance, setSelectedInsurance] =
    useState<string | undefined>(undefined);

  const [showPopupNoInsurance, setShowPopupNoInsurance] =
    useState<boolean>(false);
  //skip prestation occupant
  const [hasPresta, setHasPresta] = useState<boolean>(true);
  //skip prestation accommodation/stay
  const [hasPrestaStay, setHasPrestaStay] = useState<boolean>(true);
  //saving state
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [componentLoading, setComponentLoading] = useState<boolean>(true);
  const [loadingAddProposal, setLoadingAddProposal] = useState<boolean>(true);

  //fast chechin / checkout state
  const [selectedFastcheckout, setSelectedFastcheckout] =
    useState<boolean>(true);

  const enableFCI =
    (process.env.REACT_APP_ENABLEFCI ?? "false") === JSON.stringify(true);

  const isFromFastCheckinOrCheckout = cart?.fromFastCheckinCheckout
    ? cart.fromFastCheckinCheckout && enableFCI
    : false;

  const stepAll: ResaStep[] = [
    {
      label:
        lang.toUpperCase() === "FR"
          ? authMode === 2
            ? "Coordonnées"
            : "Authentification"
          : authMode === 2
          ? "Contact details"
          : "Authentication",
      show: true,
    },
    {
      label: lang.toUpperCase() === "FR" ? "Prestations" : "Products",
      show: true,
    },
    {
      label: lang.toUpperCase() === "FR" ? "Activités" : "Activities",
      show: true,
    },
    {
      label: lang.toUpperCase() === "FR" ? "Paiement" : "Payment",
      show: true,
    },
  ];
  const stepWithoutOccupant: ResaStep[] = [
    {
      label:
        lang.toUpperCase() === "FR"
          ? authMode === 2
            ? "Coordonnées"
            : "Authentification"
          : authMode === 2
          ? "Contact details"
          : "Authentication",
      show: true,
    },
    {
      label: lang.toUpperCase() === "FR" ? "Prestations" : "Products",
      show: true,
    },
    {
      label: lang.toUpperCase() === "FR" ? "Activités" : "Activities",
      show: false,
    },
    {
      label: lang.toUpperCase() === "FR" ? "Paiement" : "Payment",
      show: true,
    },
  ];

  const stepWithoutAuthWithoutOccupant: ResaStep[] = [
    {
      label:
        lang.toUpperCase() === "FR"
          ? authMode === 2
            ? "Coordonnées"
            : "Authentification"
          : authMode === 2
          ? "Contact details"
          : "Authentication",
      show: false,
    },
    {
      label: lang.toUpperCase() === "FR" ? "Prestations" : "Products",
      show: true,
    },
    {
      label: lang.toUpperCase() === "FR" ? "Activités" : "Activities",
      show: false,
    },
    {
      label: lang.toUpperCase() === "FR" ? "Paiement" : "Payment",
      show: true,
    },
  ];

  const stepWithoutAccommodation: ResaStep[] = [
    {
      label:
        lang.toUpperCase() === "FR"
          ? authMode === 2
            ? "Coordonnées"
            : "Authentification"
          : authMode === 2
          ? "Contact details"
          : "Authentication",
      show: true,
    },
    {
      label: lang.toUpperCase() === "FR" ? "Prestations" : "Products",
      show: false,
    },
    {
      label: lang.toUpperCase() === "FR" ? "Activités" : "Activities",
      show: true,
    },
    {
      label: lang.toUpperCase() === "FR" ? "Paiement" : "Payment",
      show: true,
    },
  ];

  const stepWithoutAuthWithoutAccommodation: ResaStep[] = [
    {
      label:
        lang.toUpperCase() === "FR"
          ? authMode === 2
            ? "Coordonnées"
            : "Authentification"
          : authMode === 2
          ? "Contact details"
          : "Authentication",
      show: false,
    },
    {
      label: lang.toUpperCase() === "FR" ? "Prestations" : "Products",
      show: false,
    },
    {
      label: lang.toUpperCase() === "FR" ? "Activités" : "Activities",
      show: true,
    },
    {
      label: lang.toUpperCase() === "FR" ? "Paiement" : "Payment",
      show: true,
    },
  ];

  const stepWithoutAccommodationWithoutOccupant: ResaStep[] = [
    {
      label:
        lang.toUpperCase() === "FR"
          ? authMode === 2
            ? "Coordonnées"
            : "Authentification"
          : authMode === 2
          ? "Contact details"
          : "Authentication",
      show: true,
    },
    {
      label: lang.toUpperCase() === "FR" ? "Prestations" : "Products",
      show: false,
    },
    {
      label: lang.toUpperCase() === "FR" ? "Activités" : "Activities",
      show: false,
    },
    {
      label: lang.toUpperCase() === "FR" ? "Paiement" : "Payment",
      show: true,
    },
  ];

  const stepWithoutAuthWithoutAccommodationWithoutOccupant: ResaStep[] = [
    {
      label:
        lang.toUpperCase() === "FR"
          ? authMode === 2
            ? "Coordonnées"
            : "Authentification"
          : authMode === 2
          ? "Contact details"
          : "Authentication",
      show: false,
    },
    {
      label: lang.toUpperCase() === "FR" ? "Prestations" : "Products",
      show: false,
    },
    {
      label: lang.toUpperCase() === "FR" ? "Activités" : "Activities",
      show: false,
    },
    {
      label: lang.toUpperCase() === "FR" ? "Paiement" : "Payment",
      show: true,
    },
  ];

  const stepWithoutAuth: ResaStep[] = [
    {
      label:
        lang.toUpperCase() === "FR"
          ? authMode === 2
            ? "Coordonnées"
            : "Authentification"
          : authMode === 2
          ? "Contact details"
          : "Authentication",
      show: false,
    },
    {
      label: lang.toUpperCase() === "FR" ? "Prestations" : "Products",
      show: true,
    },
    {
      label: lang.toUpperCase() === "FR" ? "Activités" : "Activities",
      show: true,
    },
    {
      label: lang.toUpperCase() === "FR" ? "Paiement" : "Payment",
      show: true,
    },
  ];

  const stepFast: ResaStep[] = [
    {
      label: lang.toUpperCase() === "FR" ? "Validation" : "Validation",
      show: true,
    },
    {
      label: lang.toUpperCase() === "FR" ? "Fastcheckin" : "Fastcheckin",
      show: true,
    },
    {
      label: lang.toUpperCase() === "FR" ? "Paiement" : "Payment",
      show: true,
    },
  ];

  const stepFastWithoutOccupant: ResaStep[] = [
    {
      label: lang.toUpperCase() === "FR" ? "Validation" : "Validation",
      show: false,
    },
    {
      label: lang.toUpperCase() === "FR" ? "Fastcheckin" : "Fastcheckin",
      show: true,
    },
    {
      label: lang.toUpperCase() === "FR" ? "Paiement" : "Payment",
      show: true,
    },
  ];

  const [steps, setSteps] = useState<ResaStep[]>(stepAll);
  const [mountedCart, setMountedCart] = useState<boolean>(false);
  const [mountedSteps, setMountedSteps] = useState<boolean>(false);

  const [savingStep, setSavingStep] = useState<boolean>(false);

  const [valid, setValid] = useState<boolean>(true);

  useEffect(() => {
    // Mettez à jour l'URL avec le paramètre 'step' chaque fois que 'activeStep' change
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.set("step", activeStep.toString());

    // Si le paramètre 'step' est déjà présent, mettez à jour sa valeur
    if (stepParam !== null) {
      newSearchParams.set("step", activeStep.toString());
    }

    setSearchParams(newSearchParams);
  }, [activeStep]);

  const initSteps = async () => {
    //OLD
    //Occupant
    /*
    let prestaOccupants = false;
    const occupantGuiid: number =
      cart?.reservations![0]?.stays![0]?.occupants![0]?.guiid ?? 0;

    try {
      const { data: dataOccupants } = await client.query<
        Query,
        QueryGetOccupantProductsArgs
      >({
        query: getOccupantProductsQuery(),
        variables: {
          session: { name: sessionId!! },
          input: {
            occupantGuiid: occupantGuiid,
          },
        },
        fetchPolicy: "no-cache",
      });

      if (
        dataOccupants &&
        dataOccupants.getOccupantProducts &&
        (dataOccupants.getOccupantProducts.occupantProducts?.length ?? -1) > 0
      ) {
        prestaOccupants = true;
      } else {
        prestaOccupants = false;
      }
    } catch (error) {
      prestaOccupants = false;
    }
    */

    let prestaOccupants = false;
    let prestaStay = false;

    if (cart && !isEmpty(cart.reservations)) {
      //Occupant

      //On check presta occupant:
      //NEW
      try {
        const { data: dataHasProductsForOccupantWithoutTaxes } =
          await client.query<
            Query,
            QueryHasProductsForOccupantWithoutTaxesArgs
          >({
            query: hasProductsForOccupantWithoutTaxesQuery(),
            variables: {
              session: { name: sessionId },
              input: {
                occupantGuiid:
                  cart?.reservations?.[0]?.stays?.[0]?.occupants?.[0]?.guiid!!,
              },
            },
            fetchPolicy: "no-cache",
          });

        if (
          dataHasProductsForOccupantWithoutTaxes &&
          dataHasProductsForOccupantWithoutTaxes.getOccupantProducts &&
          dataHasProductsForOccupantWithoutTaxes.getOccupantProducts
            .occupantProducts
        ) {
          // Fonction pour vérifier si au moins un produit a un code différent de  obligatoire
          const hasDifferentProductThanMandatory = (
            products: Maybe<Maybe<Product>[]> | undefined
          ): boolean => {
            return products?.some((product) => !product?.mandatory) ?? false;
          };

          for (const occupantProduct of dataHasProductsForOccupantWithoutTaxes
            .getOccupantProducts.occupantProducts) {
            if (hasDifferentProductThanMandatory(occupantProduct?.products)) {
              prestaOccupants = true;
              break;
            }
          }
        }
      } catch (error) {
        prestaOccupants = false;
      }
      //OLD
      /*
      OLD
      try {
        const { data: dataHasProductsForOccupant } = await client.query<
          Query,
          QueryHasProductsForOccupantArgs
        >({
          query: hasProductsForOccupantQuery(),
          variables: {
            session: { name: sessionId },
          },
          fetchPolicy: "no-cache",
        });

        if (
          dataHasProductsForOccupant &&
          dataHasProductsForOccupant.hasProductsForOccupant &&
          dataHasProductsForOccupant.hasProductsForOccupant
            .hasProductsForOccupant
        ) {
          prestaOccupants =
            dataHasProductsForOccupant.hasProductsForOccupant
              .hasProductsForOccupant;
        }
      } catch (error) {
        prestaOccupants = false;
      }*/

      //Stay
      const stayGuiid: number = cart?.reservations![0]?.stays![0]?.guiid ?? 0;
      try {
        const { data: dataStay } = await client.query<
          Query,
          QueryGetAccommodationProductsArgs
        >({
          query: getAccommodationProductsForStepsQuery(),
          variables: {
            session: { name: sessionId!! },
            input: {
              stayGuiid: stayGuiid,
            },
          },
          fetchPolicy: "no-cache",
        });

        if (
          dataStay &&
          dataStay.getAccommodationProducts &&
          (dataStay.getAccommodationProducts.accommodationProducts?.length ??
            -1) > 0
        ) {
          prestaStay = true;
        } else {
          prestaStay = false;
        }
      } catch (error) {
        prestaStay = false;
      }
    }

    if (authMode === 1 && user) {
      if (!isFromFastCheckinOrCheckout) {
        if (!prestaOccupants && !prestaStay) {
          setSteps(stepWithoutAuthWithoutAccommodationWithoutOccupant);
        } else if (prestaOccupants && !prestaStay) {
          setSteps(stepWithoutAuthWithoutAccommodation);
        } else if (!prestaOccupants && prestaStay) {
          setSteps(stepWithoutAuthWithoutOccupant);
        } else if (prestaOccupants && prestaStay) {
          setSteps(stepWithoutAuth);
        }
      }
    } else {
      if (!isFromFastCheckinOrCheckout) {
        if (prestaOccupants && prestaStay) {
          setSteps(stepAll);
        } else if (!prestaOccupants && !prestaStay) {
          setSteps(stepWithoutAccommodationWithoutOccupant);
        } else if (prestaOccupants && !prestaStay) {
          setSteps(stepWithoutAccommodation);
        } else if (!prestaOccupants && prestaStay) {
          setSteps(stepWithoutOccupant);
        }
      } else {
        if (prestaOccupants) {
          setSteps(stepFast);
        } else {
          setSteps(stepFastWithoutOccupant);
        }
      }
    }

    setHasPresta(prestaOccupants);
    setHasPrestaStay(prestaStay);
    setComponentLoading(false);
  };

  useEffect(() => {
    if (!componentLoading && authMode === 1 && user) {
      if (stepParam) {
        setActiveStep(parseInt(stepParam));
      } else {
        if (hasPresta && hasPrestaStay) {
          setActiveStep(StepsEnum.ACTIVITIES);
        } else if (!hasPresta && hasPrestaStay) {
          setActiveStep(StepsEnum.ACTIVITIES);
        } else if (hasPresta && !hasPrestaStay) {
          setActiveStep(StepsEnum.OCCUPANT);
        } else if (!hasPresta && !hasPrestaStay) {
          setActiveStep(StepsEnum.PAYMENT);
        }
      }
    }
  }, [authMode, componentLoading]);

  useEffect(() => {
    /* Force page refresh on navigation back button */
    window.addEventListener("pageshow", function (event) {
      var historyTraversal =
        event.persisted ||
        (typeof window.performance != "undefined" &&
          window.performance.navigation.type === 2);
      if (historyTraversal) {
        // Handle page restore.
        window.location.reload();
      }
    });

    /*Facebook Pixel Code Tracking*/
    ReactPixel.init(process.env.REACT_APP_GOOGLE_PIXEL_CODE!!);
    ReactPixel.track("AddToCart");
  }, []);

  useEffect(() => {
    if (!loadingCart) {
      //etat proposalkey a changé ou non
      let proposalKeyChanged = false;
      //on recup la proposalkey dans les params
      const paramProposalKey = searchParams.get("proposal_key");
      //on recup la proposalkey dans le localstorage (ajout lors de chooseProposalFromKeys)
      const localProposalKey = getLocalStorageItem("rsl_proposal_key") ?? null;
      //on regarde si on a un proposal key dans le localstorage
      let p_key: string | null = null;
      //si proposal key dans le localstorage, alors on set la valeur de p avec la valeur du localstorage
      if (localProposalKey && localProposalKey !== null) {
        p_key = JSON.parse(localProposalKey)[0] as string;
      }
      //on verifie que p n'est pas vide
      if (!isEmpty(p_key)) {
        //on check si la proposalkey du localstorage est égal a la proposalkey en param url
        //si c'est la meme proposalkey, alors changed = false, sinon changed = true
        if (p_key!! === paramProposalKey) {
          proposalKeyChanged = false;
        } else {
          proposalKeyChanged = true;
        }
      }
      new URLSearchParams(window.location.search).get("debug") === "true" &&
        console.info("La proposal key a changé: ", proposalKeyChanged);
      //si on a un panier et des résas
      if (
        cart &&
        Array.isArray(cart.reservations) &&
        cart.reservations.length > 0
      ) {
        //si la proposalkey a changé et qu'on a un param proposalkey, on ajoute la proposalkey au panier
        if (proposalKeyChanged && paramProposalKey) {
          new URLSearchParams(window.location.search).get("debug") === "true" &&
            console.info(
              "La proposalKey a changé, on fait un addProposalFromKey"
            );
          addProposalFromKeys(
            [paramProposalKey],
            undefined,
            (err, response) => {
              if (err) {
                toast.error(err.message);
                console.log(err);
              }
              setLoadingAddProposal(false);
              setMountedCart(true);
            }
          );
          //sinon, on a un panier mais la proposalKey n'a pas changé, on init les step
        } else {
          new URLSearchParams(window.location.search).get("debug") === "true" &&
            console.info("La proposalKey n'a pas changé, on init les step");
          setLoadingAddProposal(false);
          setMountedCart(true);
        }
        //sinon, si pas de panier, on ajoute la proposalkey au panier
      } else {
        if (paramProposalKey) {
          new URLSearchParams(window.location.search).get("debug") === "true" &&
            console.info("On a pas de panier, on fait un addProposalFromKey");
          addProposalFromKeys(
            [paramProposalKey],
            undefined,
            (err, response) => {
              if (err) {
                toast.error(err.message);
                console.log(err);
              }
              setLoadingAddProposal(false);
              setMountedCart(true);
            }
          );
        } else {
          setLoadingAddProposal(false);
          setMountedCart(true);
        }
      }
    }
  }, [loadingCart]);

  useLayoutEffect(() => {
    const init = async () => {
      await initSteps();
      setMountedSteps(true);
    };

    const checkValidity = () => {
      let bool = true;
      cart?.reservations?.forEach((res) => {
        res?.stays?.map((stay) => {
          let occupant = stay?.occupants?.length ?? 0;
          if (occupant <= 0) {
            bool = false;
            return;
          }
        });
      });
      return bool;
    };

    if (cart) {
      if (mountedCart) {
        if (!mountedSteps) {
          init();
        }
        setSelectedInsurance(
          getLocalStorageItem("product_insurance") ?? undefined
        );
      }
      setSelectedPaymentDueType(
        cart?.reservations?.[0]?.selectedPaymentDue ?? undefined
      );

      setValid(checkValidity());
    }
  }, [cart, mountedCart]);

  useEffect(() => {
    if (cart && !isEmpty(cart.reservations)) {
      const pspStatus = searchParams.get("s");
      let pspMessage = searchParams.get("pspCallbackMessage");
      if (pspStatus && !isFromFastCheckinOrCheckout) {
        switch (pspStatus as PspCallbackStatus) {
          case "s":
            pspMessage = pspMessage
              ? pspMessage
              : getLanguageVarValue("LANG_neobooking_psp_success");
            break;
          case "d":
            pspMessage = pspMessage
              ? pspMessage
              : getLanguageVarValue("LANG_neobooking_psp_decline");
            break;
          case "c":
            pspMessage = pspMessage
              ? pspMessage
              : getLanguageVarValue("LANG_neobooking_psp_cancel");
            break;
          case "e":
            pspMessage = pspMessage
              ? pspMessage
              : getLanguageVarValue("LANG_neobooking_psp_exception");
            break;
        }
        setActiveStep(StepsEnum.PAYMENT);
        setErrorsSteps([
          {
            stepIndex: StepsEnum.PAYMENT,
            variant: "warning",
            children: <>{pspMessage}</>,
          },
        ]);
      }
    } else {
      if (isFromFastCheckinOrCheckout) {
        setActiveStep(StepsEnum.OCCUPANT);
      }
      //setActiveStep(StepsEnum.AUTH);
    }
  }, [cart]);

  useEffect(() => {
    if (!mountedSteps) {
      if (stepParam) {
        setActiveStep(parseInt(stepParam));
      }
    } else {
      if (authMode === 1 && parseInt(stepParam ?? "0") === 0) {
        setActiveStep(
          hasPrestaStay
            ? StepsEnum.ACTIVITIES
            : hasPresta
            ? StepsEnum.OCCUPANT
            : StepsEnum.PAYMENT
        );
      }
    }
  }, [steps, mountedSteps]);

  useEffect(() => {
    setActiveErrorsSteps(
      [...errorsSteps].find((x) => x.stepIndex === activeStep)
    );
  }, [errorsSteps, activeStep]);

  const showInsurance = useMemo(() => {
    let show = false;
    let totaux = 0;
    if (authMode !== 2) {
      for (let i = 0; i < (cart?.reservations?.length ?? -1); i++) {
        let currentReservation = cart?.reservations?.[i];
        if (currentReservation) {
          for (let j = 0; j < (currentReservation?.stays?.length ?? -1); j++) {
            let currentStay = currentReservation?.stays?.[j];
            for (let k = 0; k < (currentStay?.occupants?.length ?? -1); k++) {
              let currentOccupant = currentStay?.occupants?.[k];
              if (currentOccupant) {
                let total =
                  currentOccupant?.paymentDues?.reduce((prev, acc) => {
                    return prev + (acc?.receivedAmount?.amount ?? 0);
                  }, 0) ?? 0;

                totaux += total;
              }
            }
          }
        }
      }
      return (show = totaux === 0);
    } else {
      return false;
    }
  }, [cart]);

  const getWebUser = () => {
    const localWebuser = getLocalStorageItem("webuser");
    return (
      searchParams.get("webuser") ??
      searchParams.get("wu") ??
      localWebuser ??
      process.env.REACT_APP_WEBUSER!!
    );
  };

  const handlePreviousStep = () => {
    let category =
      activeStep === StepsEnum.ACTIVITIES
        ? "Options"
        : activeStep === StepsEnum.OCCUPANT
        ? "Occupant"
        : activeStep === StepsEnum.AUTH
        ? "Authentification"
        : "Paiement";
    trackClick(
      `Tunnel - ${category}`,
      "click bouton",
      "Bouton - Précédent",
      ""
    );

    if (authMode === 1 && user) {
      if (!isFromFastCheckinOrCheckout) {
        if (activeStep === StepsEnum.PAYMENT) {
          if (hasPresta && hasPrestaStay) {
            setActiveStep(StepsEnum.OCCUPANT);
          } else if (!hasPresta && !hasPrestaStay) {
            setActiveStep(StepsEnum.PAYMENT);
          } else if (!hasPresta && hasPrestaStay) {
            setActiveStep(StepsEnum.ACTIVITIES);
          } else if (hasPresta && !hasPrestaStay) {
            setActiveStep(StepsEnum.OCCUPANT);
          }
        } else if (activeStep === StepsEnum.OCCUPANT) {
          if (hasPrestaStay) {
            setActiveStep(StepsEnum.ACTIVITIES);
          }
        }
      }
    } else {
      if (!isFromFastCheckinOrCheckout) {
        if (activeStep === StepsEnum.PAYMENT) {
          if (hasPresta && hasPrestaStay) {
            setActiveStep(StepsEnum.OCCUPANT);
          } else if (!hasPresta && !hasPrestaStay) {
            setActiveStep(StepsEnum.AUTH);
          } else if (!hasPresta && hasPrestaStay) {
            setActiveStep(StepsEnum.ACTIVITIES);
          } else if (hasPresta && !hasPrestaStay) {
            setActiveStep(StepsEnum.OCCUPANT);
          }
        } else if (activeStep === StepsEnum.OCCUPANT) {
          if (!hasPrestaStay && hasPresta) {
            setActiveStep(StepsEnum.AUTH);
          } else if (hasPrestaStay && !hasPresta) {
            setActiveStep(StepsEnum.ACTIVITIES);
          } else if (!hasPresta && !hasPrestaStay) {
            setActiveStep(StepsEnum.AUTH);
          } else if (hasPresta && hasPrestaStay) {
            setActiveStep(StepsEnum.ACTIVITIES);
          }
        } else if (activeStep === StepsEnum.ACTIVITIES) {
          setActiveStep(StepsEnum.AUTH);
        }
      } else {
        if (activeStep === 2) {
          setActiveStep(1);
        } else if (activeStep === 1) {
          if (hasPresta) {
            setActiveStep(0);
          }
        }
      }
    }
  };
  const handleNextStep = async (skipInsurance?: boolean | undefined) => {
    let _errorsSteps: TErrorsSteps[] = [...errorsSteps].filter(
      (x) => x.stepIndex !== activeStep
    );

    if (authMode !== 2) {
      if (!isFromFastCheckinOrCheckout && authMode === 1 && user) {
        switch (activeStep) {
          case StepsEnum.AUTH:
            setActiveStep(
              hasPrestaStay
                ? StepsEnum.ACTIVITIES
                : hasPresta
                ? StepsEnum.OCCUPANT
                : StepsEnum.PAYMENT
            );
            break;
          case StepsEnum.ACTIVITIES:
            setActiveStep(hasPresta ? StepsEnum.OCCUPANT : StepsEnum.PAYMENT);
            trackClick(
              "Tunnel - Occupant",
              "click bouton",
              "Bouton - Suivant",
              ""
            );
            break;
          case StepsEnum.OCCUPANT:
            const { valid, controlToFocus, hasPrestaOccupant } =
              checkOccupant();
            if (hasPrestaOccupant && !valid) {
              toast.error(
                getLanguageVarValue(
                  "LANG_neobooking_complete_all_occupants_details"
                )
              );
              _errorsSteps.push({
                stepIndex: activeStep,
                variant: "warning",
                children: (
                  <>
                    {getLanguageVarValue(
                      "LANG_neobooking_complete_all_occupants_details"
                    )}
                  </>
                ),
              });
              scrollTop();
              focusElementById(controlToFocus);
            } else {
              setActiveStep(activeStep + 1);
              trackClick(
                "Tunnel - Paiement",
                "click bouton",
                "Bouton - Suivant",
                ""
              );
            }
            setErrorsSteps(_errorsSteps);
            break;
          case StepsEnum.PAYMENT:
            let stepErrorMessage = "";
            if (!termsChecked) {
              stepErrorMessage += getLanguageVarValue(
                "LANG_neobooking_terms_required"
              );
              setTermsError(true);
            } else {
              setErrorsSteps(_errorsSteps);
            }
            if (!isEmpty(stepErrorMessage)) {
              toast.error(stepErrorMessage);
              _errorsSteps.push({
                stepIndex: activeStep,
                variant: "warning",
                children: <>{stepErrorMessage}</>,
              });
              setErrorsSteps(_errorsSteps);
              return;
            }

            if (
              selectedInsurance === undefined &&
              skipInsurance === true &&
              showInsurance
            ) {
              //console.log(selectedInsurance);
              setShowPopupNoInsurance(true);
            } else {
              // if (isEmpty(cart?.reservations?.[0]?.selectedPaymentDue)) {
              if (isEmpty(selectedPaymentDueType)) {
                stepErrorMessage += `${getLanguageVarValue(
                  "LANG_neobooking_payment_due_required"
                )} `;
              }
              if (!isEmpty(stepErrorMessage)) {
                toast.error(stepErrorMessage);
                _errorsSteps.push({
                  stepIndex: activeStep,
                  variant: "warning",
                  children: <>{stepErrorMessage}</>,
                });
                setErrorsSteps(_errorsSteps);
              } else {
                setTermsError(false);
                setIsSaving(true);
                const reservationUrl =
                  window.location.origin +
                  `/reservation?step=${(steps.length - 1).toString()}`;
                const confirmationUrl =
                  window.location.origin + "/confirmation";

                const reservationId = cart?.reservations!![0]?.id;

                const acceptUrl = `${confirmationUrl}?s=s&r=${reservationId}&wu=${getWebUser()}`;
                const declineUrl = `${reservationUrl}&s=d&session=${sessionId}&r=${reservationId}&wu=${getWebUser()}`;
                const cancelUrl = `${reservationUrl}&s=c&session=${sessionId}&r=${reservationId}&wu=${getWebUser()}`;
                const exceptionUrl = `${reservationUrl}&s=e&session=${sessionId}&r=${reservationId}&wu=${getWebUser()}`;

                const isAutomaticPayment = true;
                const input: CartPaymentMethodInput = {
                  bookingStatus: BookingStatus.Guaranteed,
                  ccardType: parseInt(selectedPaymentOption ?? "1"),
                  paymentMode: "CB",
                  paymentOptions: "CB",
                  automaticPayment: true, //cart.payment.isAuto
                  //manualPaymentOptions: null,
                  //paymentModeSetOnlyFirst: null,
                  //paymentAmount: null,
                  //chequeNumber: null,
                  url: {
                    acceptUrl,
                    declineUrl,
                    cancelUrl,
                    exceptionUrl,
                  },
                };

                //Avant de save ou de redirect sur psp, on check les values de l'occupant
                try {
                  const { data: cartData } = await client.query<
                    Query,
                    QueryGetCartArgs
                  >({
                    query: getCartQuery(),
                    variables: {
                      session: { name: sessionId },
                    },
                    fetchPolicy: "no-cache",
                  });

                  if (
                    cartData &&
                    cartData.getCart &&
                    cartData.getCart.cart &&
                    cartData.getCart.cart &&
                    cartData.getCart.cart.reservations &&
                    cartData.getCart.cart.reservations.length > 0
                  ) {
                    //si on a un cart, on check les values de l'occupant
                    const lastname =
                      cartData.getCart.cart.reservations[0]?.primaryOccupant
                        ?.lastName;
                    const isLastnameEmpty = isEmpty(lastname);
                    //Si on a un lastname primary occupant vide, on fait un updateOccupant
                    if (isLastnameEmpty) {
                      const { data: dataUpdateOccupant, errors } =
                        await client.mutate<
                          Mutation,
                          MutationUpdateOccupantArgs
                        >({
                          mutation: updateOccupantMutation(),
                          variables: {
                            session: { name: sessionId },
                            input: {
                              occupantGuiid:
                                cartData.getCart.cart.reservations[0]
                                  ?.stays?.[0]?.occupants?.[0]?.guiid!!,
                              firstName: user?.firstName,
                              lastName: user?.lastName,
                              birthDate: user?.birthDate,
                            },
                          },
                          fetchPolicy: "no-cache",
                        });
                    }
                  }
                } catch (error) {
                  console.log(error);
                }

                if (isAutomaticPayment) {
                  client
                    .mutate<
                      Mutation,
                      MutationRedirectPaymentGatewayForCartArgs
                    >({
                      mutation: redirectPaymentGatewayForCartMutation(),
                      variables: {
                        session: { name: sessionId!! },
                        input: { input: input },
                      },
                      fetchPolicy: "no-cache",
                    })
                    .then((response) => {
                      if (
                        response.data?.redirectPaymentGatewayForCart?.gateway
                      ) {
                        setIsSaving(false);
                        ReactPixel.track("InitiateCheckout");
                        trackClick(
                          "Tunnel - Paiement",
                          "click bouton",
                          "Bouton - Réserver",
                          ""
                        );
                        window.location.href =
                          response.data.redirectPaymentGatewayForCart?.gateway;
                      }
                    })
                    .catch((err) => {
                      toast.error(err.message);
                      _errorsSteps.push({
                        stepIndex: activeStep,
                        variant: "warning",
                        children: <>{err.message}</>,
                      });
                      setErrorsSteps(_errorsSteps);
                      setIsSaving(false);
                    });
                } else {
                  save(false, cart?.fromFastCheckinCheckout ?? false, (err) => {
                    if (err) {
                      toast.error(err.message);
                      _errorsSteps.push({
                        stepIndex: activeStep,
                        variant: "warning",
                        children: <>{err.message}</>,
                      });
                      setErrorsSteps(_errorsSteps);
                      setIsSaving(false);
                    } else {
                      trackClick(
                        "Tunnel - Paiement",
                        "click bouton",
                        "Bouton - Réserver",
                        ""
                      );
                      ReactPixel.track("InitiateCheckout");
                      window.location.href = confirmationUrl;
                    }
                  });
                }
              }
            }
            break;
        }
      } else if (!isFromFastCheckinOrCheckout) {
        switch (activeStep) {
          case StepsEnum.ACTIVITIES:
            setActiveStep(hasPresta ? StepsEnum.OCCUPANT : StepsEnum.PAYMENT);
            trackClick(
              "Tunnel - Occupant",
              "click bouton",
              "Bouton - Suivant",
              ""
            );
            break;
          case StepsEnum.OCCUPANT:
            const { valid, controlToFocus, hasPrestaOccupant } =
              checkOccupant();
            if (hasPrestaOccupant && !valid) {
              toast.error(
                getLanguageVarValue(
                  "LANG_neobooking_complete_all_occupants_details"
                )
              );
              _errorsSteps.push({
                stepIndex: activeStep,
                variant: "warning",
                children: (
                  <>
                    {getLanguageVarValue(
                      "LANG_neobooking_complete_all_occupants_details"
                    )}
                  </>
                ),
              });
              scrollTop();
              focusElementById(controlToFocus);
            } else {
              setActiveStep(activeStep + 1);
              trackClick(
                "Tunnel - Paiement",
                "click bouton",
                "Bouton - Suivant",
                ""
              );
            }
            setErrorsSteps(_errorsSteps);
            break;
          case StepsEnum.AUTH:
            if (isEmpty(user)) {
              toast.error(
                getLanguageVarValue("LANG_neobooking_auth_required_to_continue")
              );
              _errorsSteps.push({
                stepIndex: activeStep,
                variant: "warning",
                children: (
                  <>
                    {getLanguageVarValue(
                      "LANG_neobooking_auth_required_to_continue"
                    )}
                  </>
                ),
              });
            } else {
              if (hasPresta && hasPrestaStay) {
                setActiveStep(StepsEnum.ACTIVITIES);
              } else if (!hasPresta && !hasPrestaStay) {
                setActiveStep(StepsEnum.PAYMENT);
              } else if (!hasPresta && hasPrestaStay) {
                setActiveStep(StepsEnum.ACTIVITIES);
              } else if (hasPresta && !hasPrestaStay) {
                setActiveStep(StepsEnum.OCCUPANT);
              }
              trackClick(
                "Tunnel - Options",
                "click bouton",
                "Bouton - Suivant",
                ""
              );
            }
            setErrorsSteps(_errorsSteps);
            break;
          case StepsEnum.PAYMENT:
            let stepErrorMessage = "";
            if (!termsChecked) {
              stepErrorMessage += getLanguageVarValue(
                "LANG_neobooking_terms_required"
              );
              setTermsError(true);
            } else {
              setErrorsSteps(_errorsSteps);
            }
            if (!isEmpty(stepErrorMessage)) {
              toast.error(stepErrorMessage);
              _errorsSteps.push({
                stepIndex: activeStep,
                variant: "warning",
                children: <>{stepErrorMessage}</>,
              });
              setErrorsSteps(_errorsSteps);
              return;
            }

            if (
              selectedInsurance === undefined &&
              skipInsurance === true &&
              showInsurance
            ) {
              //console.log(selectedInsurance);
              setShowPopupNoInsurance(true);
            } else {
              // if (isEmpty(cart?.reservations?.[0]?.selectedPaymentDue)) {
              if (isEmpty(selectedPaymentDueType)) {
                stepErrorMessage += `${getLanguageVarValue(
                  "LANG_neobooking_payment_due_required"
                )} `;
              }
              if (!isEmpty(stepErrorMessage)) {
                toast.error(stepErrorMessage);
                _errorsSteps.push({
                  stepIndex: activeStep,
                  variant: "warning",
                  children: <>{stepErrorMessage}</>,
                });
                setErrorsSteps(_errorsSteps);
              } else {
                setTermsError(false);
                setIsSaving(true);
                const reservationUrl =
                  window.location.origin +
                  `/reservation?step=${(steps.length - 1).toString()}`;
                const confirmationUrl =
                  window.location.origin + "/confirmation";

                const reservationId = cart?.reservations!![0]?.id;

                const acceptUrl = `${confirmationUrl}?s=s&r=${reservationId}&wu=${getWebUser()}`;
                const declineUrl = `${reservationUrl}&s=d&session=${sessionId}&r=${reservationId}&wu=${getWebUser()}`;
                const cancelUrl = `${reservationUrl}&s=c&session=${sessionId}&r=${reservationId}&wu=${getWebUser()}`;
                const exceptionUrl = `${reservationUrl}&s=e&session=${sessionId}&r=${reservationId}&wu=${getWebUser()}`;

                const isAutomaticPayment = true;
                const input: CartPaymentMethodInput = {
                  bookingStatus: BookingStatus.Guaranteed,
                  ccardType: parseInt(selectedPaymentOption ?? "1"),
                  paymentMode: "CB",
                  paymentOptions: "CB",
                  automaticPayment: true, //cart.payment.isAuto
                  //manualPaymentOptions: null,
                  //paymentModeSetOnlyFirst: null,
                  //paymentAmount: null,
                  //chequeNumber: null,
                  url: {
                    acceptUrl,
                    declineUrl,
                    cancelUrl,
                    exceptionUrl,
                  },
                };

                //Avant de save ou de redirect sur psp, on check les values de l'occupant
                try {
                  const { data: cartData } = await client.query<
                    Query,
                    QueryGetCartArgs
                  >({
                    query: getCartQuery(),
                    variables: {
                      session: { name: sessionId },
                    },
                    fetchPolicy: "no-cache",
                  });

                  if (
                    cartData &&
                    cartData.getCart &&
                    cartData.getCart.cart &&
                    cartData.getCart.cart &&
                    cartData.getCart.cart.reservations &&
                    cartData.getCart.cart.reservations.length > 0
                  ) {
                    //si on a un cart, on check les values de l'occupant
                    const lastname =
                      cartData.getCart.cart.reservations[0]?.primaryOccupant
                        ?.lastName;
                    const isLastnameEmpty = isEmpty(lastname);
                    //Si on a un lastname primary occupant vide, on fait un updateOccupant
                    if (isLastnameEmpty) {
                      const { data: dataUpdateOccupant, errors } =
                        await client.mutate<
                          Mutation,
                          MutationUpdateOccupantArgs
                        >({
                          mutation: updateOccupantMutation(),
                          variables: {
                            session: { name: sessionId },
                            input: {
                              occupantGuiid:
                                cartData.getCart.cart.reservations[0]
                                  ?.stays?.[0]?.occupants?.[0]?.guiid!!,
                              firstName: user?.firstName,
                              lastName: user?.lastName,
                              birthDate: user?.birthDate,
                            },
                          },
                          fetchPolicy: "no-cache",
                        });
                    }
                  }
                } catch (error) {
                  console.log(error);
                }

                if (isAutomaticPayment) {
                  client
                    .mutate<
                      Mutation,
                      MutationRedirectPaymentGatewayForCartArgs
                    >({
                      mutation: redirectPaymentGatewayForCartMutation(),
                      variables: {
                        session: { name: sessionId!! },
                        input: { input: input },
                      },
                      fetchPolicy: "no-cache",
                    })
                    .then((response) => {
                      if (
                        response.data?.redirectPaymentGatewayForCart?.gateway
                      ) {
                        setIsSaving(false);
                        ReactPixel.track("InitiateCheckout");
                        trackClick(
                          "Tunnel - Paiement",
                          "click bouton",
                          "Bouton - Réserver",
                          ""
                        );
                        window.location.href =
                          response.data.redirectPaymentGatewayForCart?.gateway;
                      }
                    })
                    .catch((err) => {
                      toast.error(err.message);
                      _errorsSteps.push({
                        stepIndex: activeStep,
                        variant: "warning",
                        children: <>{err.message}</>,
                      });
                      setErrorsSteps(_errorsSteps);
                      setIsSaving(false);
                    });
                } else {
                  save(false, cart?.fromFastCheckinCheckout ?? false, (err) => {
                    if (err) {
                      toast.error(err.message);
                      _errorsSteps.push({
                        stepIndex: activeStep,
                        variant: "warning",
                        children: <>{err.message}</>,
                      });
                      setErrorsSteps(_errorsSteps);
                      setIsSaving(false);
                    } else {
                      trackClick(
                        "Tunnel - Paiement",
                        "click bouton",
                        "Bouton - Réserver",
                        ""
                      );
                      ReactPixel.track("InitiateCheckout");
                      window.location.href = confirmationUrl;
                    }
                  });
                }
              }
            }
            break;
        }
      } else {
        switch (activeStep) {
          case 0:
            const { valid, controlToFocus, hasPrestaOccupant } =
              checkOccupant();
            if (hasPrestaOccupant && !valid) {
              toast.error(
                getLanguageVarValue(
                  "LANG_neobooking_complete_all_occupants_details"
                )
              );
              _errorsSteps.push({
                stepIndex: activeStep,
                variant: "warning",
                children: (
                  <>
                    {getLanguageVarValue(
                      "LANG_neobooking_complete_all_occupants_details"
                    )}
                  </>
                ),
              });
              scrollTop();
              focusElementById(controlToFocus);
            } else {
              setActiveStep(activeStep + 1);
              trackClick(
                "Tunnel - Paiement",
                "click bouton",
                "Bouton - Suivant",
                ""
              );
            }
            setErrorsSteps(_errorsSteps);
            break;
          case 1:
            setSavingStep(true);
            confirmFastCheckout(selectedFastcheckout, (err) => {
              if (err) {
                toast.error(err.message);
                _errorsSteps.push({
                  stepIndex: activeStep,
                  variant: "warning",
                  children: <>{err.message}</>,
                });
              } else {
                setActiveStep(activeStep + 1);
              }
              setSavingStep(false);
            });
            break;
          case 2:
            //console.log(selectedInsurance);
            //console.log(skipInsurance);
            let stepErrorMessage = "";
            if (!termsChecked) {
              stepErrorMessage += getLanguageVarValue(
                "LANG_neobooking_terms_required"
              );
              setTermsError(true);
            } else {
              setErrorsSteps(_errorsSteps);
            }
            if (!isEmpty(stepErrorMessage)) {
              toast.error(stepErrorMessage);
              _errorsSteps.push({
                stepIndex: activeStep,
                variant: "warning",
                children: <>{stepErrorMessage}</>,
              });
              setErrorsSteps(_errorsSteps);
              return;
            }
            if (
              selectedInsurance === undefined &&
              skipInsurance === true &&
              showInsurance
            ) {
              //console.log(selectedInsurance);
              setShowPopupNoInsurance(true);
            } else {
              // if (isEmpty(cart?.reservations?.[0]?.selectedPaymentDue)) {
              if (isEmpty(selectedPaymentDueType)) {
                stepErrorMessage += `${getLanguageVarValue(
                  "LANG_neobooking_payment_due_required"
                )} `;
              }
              if (!isEmpty(stepErrorMessage)) {
                toast.error(stepErrorMessage);
                _errorsSteps.push({
                  stepIndex: activeStep,
                  variant: "warning",
                  children: <>{stepErrorMessage}</>,
                });
                setErrorsSteps(_errorsSteps);
              } else {
                setTermsError(false);
                setIsSaving(true);
                const reservationUrl =
                  window.location.origin +
                  `/reservation?step=${(steps.length - 1).toString()}`;
                const confirmationUrl =
                  window.location.origin + "/confirmation";

                const reservationId = cart?.reservations!![0]?.id;

                const acceptUrl = `${confirmationUrl}?s=s&session=${sessionId}&reservationId=${reservationId}&wu=${getWebUser()}`;
                const declineUrl = `${reservationUrl}&s=d&session=${sessionId}&reservationId=${reservationId}&pi=true&wu=${getWebUser()}`;
                const cancelUrl = `${reservationUrl}&s=c&session=${sessionId}&reservationId=${reservationId}&pi=true&wu=${getWebUser()}`;
                const exceptionUrl = `${reservationUrl}&s=e&session=${sessionId}&reservationId=${reservationId}&pi=true&wu=${getWebUser()}`;

                const isAutomaticPayment = true;
                const input: CartPaymentMethodInput = {
                  bookingStatus: BookingStatus.Guaranteed,
                  ccardType: parseInt(selectedPaymentOption ?? "1"),
                  paymentMode: "CB",
                  paymentOptions: "CB",
                  automaticPayment: true, //cart.payment.isAuto
                  //manualPaymentOptions: null,
                  //paymentModeSetOnlyFirst: null,
                  //paymentAmount: null,
                  //chequeNumber: null,
                  url: {
                    acceptUrl,
                    declineUrl,
                    cancelUrl,
                    exceptionUrl,
                  },
                };
                if (isAutomaticPayment) {
                  client
                    .mutate<
                      Mutation,
                      MutationRedirectPaymentGatewayForCartArgs
                    >({
                      mutation: redirectPaymentGatewayForCartMutation(),
                      variables: {
                        session: { name: sessionId!! },
                        input: { input: input },
                      },
                      fetchPolicy: "no-cache",
                    })
                    .then((response) => {
                      if (
                        response.data?.redirectPaymentGatewayForCart?.gateway
                      ) {
                        setIsSaving(false);
                        ReactPixel.track("InitiateCheckout");
                        trackClick(
                          "Tunnel - Paiement",
                          "click bouton",
                          "Bouton - Réserver",
                          ""
                        );
                        window.location.href =
                          response.data.redirectPaymentGatewayForCart?.gateway;
                      }
                    })
                    .catch((err) => {
                      toast.error(err.message);
                      _errorsSteps.push({
                        stepIndex: activeStep,
                        variant: "warning",
                        children: <>{err.message}</>,
                      });
                      setErrorsSteps(_errorsSteps);
                      setIsSaving(false);
                    });
                } else {
                  save(false, cart?.fromFastCheckinCheckout ?? false, (err) => {
                    if (err) {
                      toast.error(err.message);
                      _errorsSteps.push({
                        stepIndex: activeStep,
                        variant: "warning",
                        children: <>{err.message}</>,
                      });
                      setErrorsSteps(_errorsSteps);
                      setIsSaving(false);
                    } else {
                      trackClick(
                        "Tunnel - Paiement",
                        "click bouton",
                        "Bouton - Réserver",
                        ""
                      );
                      ReactPixel.track("InitiateCheckout");
                      window.location.href = confirmationUrl;
                    }
                  });
                }
              }
            }
            break;
        }
      }
    } else if (authMode === 2) {
      //Théoriquement on est deja connecté.
      switch (activeStep) {
        case StepsEnum.AUTH:
          setActiveStep(
            hasPrestaStay
              ? StepsEnum.ACTIVITIES
              : hasPresta
              ? StepsEnum.OCCUPANT
              : StepsEnum.PAYMENT
          );
          break;
        case StepsEnum.ACTIVITIES:
          setActiveStep(hasPresta ? StepsEnum.OCCUPANT : StepsEnum.PAYMENT);
          trackClick(
            "Tunnel - Occupant",
            "click bouton",
            "Bouton - Suivant",
            ""
          );
          break;
        case StepsEnum.OCCUPANT:
          const { valid, controlToFocus, hasPrestaOccupant } = checkOccupant();
          if (hasPrestaOccupant && !valid) {
            toast.error(
              getLanguageVarValue(
                "LANG_neobooking_complete_all_occupants_details"
              )
            );
            _errorsSteps.push({
              stepIndex: activeStep,
              variant: "warning",
              children: (
                <>
                  {getLanguageVarValue(
                    "LANG_neobooking_complete_all_occupants_details"
                  )}
                </>
              ),
            });
            scrollTop();
            focusElementById(controlToFocus);
          } else {
            setActiveStep(activeStep + 1);
            trackClick(
              "Tunnel - Paiement",
              "click bouton",
              "Bouton - Suivant",
              ""
            );
          }
          setErrorsSteps(_errorsSteps);
          break;
        case StepsEnum.PAYMENT:
          let stepErrorMessage = "";
          if (!termsChecked) {
            stepErrorMessage += getLanguageVarValue(
              "LANG_neobooking_terms_required"
            );
            setTermsError(true);
          } else {
            setErrorsSteps(_errorsSteps);
          }
          if (!isEmpty(stepErrorMessage)) {
            toast.error(stepErrorMessage);
            _errorsSteps.push({
              stepIndex: activeStep,
              variant: "warning",
              children: <>{stepErrorMessage}</>,
            });
            setErrorsSteps(_errorsSteps);
            return;
          }

          if (
            selectedInsurance === undefined &&
            skipInsurance === true &&
            showInsurance
          ) {
            //console.log(selectedInsurance);
            setShowPopupNoInsurance(true);
          } else {
            // if (isEmpty(cart?.reservations?.[0]?.selectedPaymentDue)) {
            if (isEmpty(selectedPaymentDueType)) {
              stepErrorMessage += `${getLanguageVarValue(
                "LANG_neobooking_payment_due_required"
              )} `;
            }
            if (!isEmpty(stepErrorMessage)) {
              toast.error(stepErrorMessage);
              _errorsSteps.push({
                stepIndex: activeStep,
                variant: "warning",
                children: <>{stepErrorMessage}</>,
              });
              setErrorsSteps(_errorsSteps);
            } else {
              setTermsError(false);
              setIsSaving(true);

              const confirmationUrl = window.location.origin + "/confirmation";

              const reservationId = cart?.reservations!![0]?.id;

              const acceptUrl = `${confirmationUrl}?s=s&r=${reservationId}&wu=${getWebUser()}&session=${sessionId}`;

              //On redirect en ferme directement puis redirect sur page de confirmation

              const {
                data: dataUpdateCartPaymentMethod,
                errors: errorsUpdateCartPaymentMethod,
              } = await client.mutate<
                Mutation,
                MutationUpdateCartPaymentMethodArgs
              >({
                mutation: updateCartPaymentMethodMutation(),
                variables: {
                  session: { name: sessionId },
                  input: {
                    //paymentOptions: "VIR2",
                    //automaticPayment: false,
                    forceSaveGuaranteedStatus: true,
                    bookingStatus: BookingStatus.Guaranteed,
                  },
                },
                fetchPolicy: "no-cache",
              });

              if (
                dataUpdateCartPaymentMethod &&
                dataUpdateCartPaymentMethod.updateCartPaymentMethod &&
                dataUpdateCartPaymentMethod.updateCartPaymentMethod.cart
              ) {
                let cart =
                  dataUpdateCartPaymentMethod.updateCartPaymentMethod.cart;

                //On check les infos du primaryOccupant, si on a un lastname vide, on le met a jour avec les infos du compte partenaire
                if (
                  isEmpty(cart?.reservations?.[0]?.primaryOccupant?.lastName) ||
                  isEmpty(cart?.reservations?.[0]?.primaryOccupant?.firstName)
                ) {
                  const firstname = !isEmpty(user?.firstName)
                    ? user?.firstName
                    : !isEmpty(user?.lastName)
                    ? user?.lastName
                    : user?.address?.mail;
                  const lastname = !isEmpty(user?.lastName)
                    ? user?.lastName
                    : !isEmpty(user?.firstName)
                    ? user?.firstName
                    : user?.address?.mail;

                  const { data: dataUpdateOccupant, errors } =
                    await client.mutate<Mutation, MutationUpdateOccupantArgs>({
                      mutation: updateOccupantMutation(),
                      variables: {
                        session: { name: sessionId },
                        input: {
                          occupantGuiid:
                            cart?.reservations?.[0]?.stays?.[0]?.occupants?.[0]
                              ?.guiid!!,
                          firstName: firstname,
                          lastName: lastname,
                          birthDate: user?.birthDate,
                        },
                      },
                      fetchPolicy: "no-cache",
                    });
                }

                //OK, On a MAJ le updateCartPaymentMethod, on peux faire un saveCart

                const { data: dataSaveCart, errors: errorsSaveCart } =
                  await client.mutate<Mutation, MutationSaveCartArgs>({
                    mutation: saveCartMutation(),
                    variables: {
                      session: { name: sessionId },
                      input: {
                        fastCheckinCheckout: false,
                      },
                    },
                    fetchPolicy: "no-cache",
                  });

                if (
                  dataSaveCart &&
                  dataSaveCart.saveCart &&
                  dataSaveCart.saveCart.cart
                ) {
                  //Le saveCart est ok, on redirige sur la page de confirmation
                  cart = dataSaveCart.saveCart.cart;
                  setIsSaving(false);
                  ReactPixel.track("InitiateCheckout");
                  trackClick(
                    "Tunnel - Paiement",
                    "click bouton",
                    "Bouton - Réserver",
                    ""
                  );
                  window.location.href = acceptUrl;
                } else if (errorsSaveCart && errorsSaveCart.length > 0) {
                  toast.error(errorsSaveCart[0].message);
                  _errorsSteps.push({
                    stepIndex: activeStep,
                    variant: "warning",
                    children: <>{errorsSaveCart[0].message}</>,
                  });
                  setErrorsSteps(_errorsSteps);
                  setIsSaving(false);
                }
              } else if (
                errorsUpdateCartPaymentMethod &&
                errorsUpdateCartPaymentMethod.length > 0
              ) {
                toast.error(errorsUpdateCartPaymentMethod[0].message);
                _errorsSteps.push({
                  stepIndex: activeStep,
                  variant: "warning",
                  children: <>{errorsUpdateCartPaymentMethod[0].message}</>,
                });
                setErrorsSteps(_errorsSteps);
                setIsSaving(false);
              }
            }
          }
          break;
      }
    }
  };

  const isValidPartner = () => {
    return (
      !isEmpty(cart?.reservations?.[0]?.primaryOccupant?.firstName) &&
      !isEmpty(cart?.reservations?.[0]?.primaryOccupant?.lastName) &&
      !isEmpty(cart?.reservations?.[0]?.primaryOccupant?.birthDate)
    );
  };

  //Vérifie que les infos des différents occupants sont ok
  const checkOccupant = (): {
    valid: boolean;
    controlToFocus: string | null;
    hasPrestaOccupant: boolean;
  } => {
    let valid = false;
    let controlToFocus: string | null = null;
    let hasPrestaOccupant = false;
    cart?.reservations?.forEach((reservation) => {
      reservation?.stays?.forEach((stay) => {
        stay?.occupants?.forEach((occupant) => {
          //exclure taxe de séjour
          hasPrestaOccupant =
            (occupant?.lineItems?.filter(
              (x) =>
                x?.product?.distributionType ===
                  ProductDistributionType.Occupant &&
                !TAXES_PRODUCTS.includes(x?.product?.code)
            ).length ?? -1) > 0;
          valid = isValidOccupant(
            occupant?.firstName!!,
            occupant?.lastName!!,
            occupant?.birthDate!!
          );
          if (!valid) {
            if (!controlToFocus) {
              if (isEmpty(occupant?.birthDate))
                controlToFocus = `txt_birthdate_${occupant?.guiid}`;
              if (isEmpty(occupant?.lastName))
                controlToFocus = `txt_lastname_${occupant?.guiid}`;
              if (isEmpty(occupant?.firstName))
                controlToFocus = `txt_firstname_${occupant?.guiid}`;
            }
          }
        });
      });
    });
    return { valid, controlToFocus, hasPrestaOccupant };
  };
  //Vérifie que les infos d'un occupant sont ok
  const isValidOccupant = (
    firstName: string,
    lastName: string,
    birthDate: Date
  ): boolean => {
    return !isEmpty(firstName) && !isEmpty(lastName) && !isEmpty(birthDate);
  };
  useLoadingScreen(
    isSaving,
    getLanguageVarValue("LANG_neobooking_saving_reservation_in_progress"),
    getLanguageVarValue("LANG_neobooking_please_wait")
  );
  const onNextStep = () => {
    let next = StepsEnum.OCCUPANT;
    if (!hasPresta && !hasPrestaStay) {
      next = StepsEnum.PAYMENT;
    } else if (hasPresta && !hasPrestaStay) {
      next = StepsEnum.OCCUPANT;
    } else if (!hasPresta && hasPrestaStay) {
      next = StepsEnum.ACTIVITIES;
    } else if (hasPresta && hasPrestaStay) {
      next = StepsEnum.ACTIVITIES;
    }
    setActiveStep(next);
  };
  if (userLoading || componentLoading || loadingCart || loadingAddProposal)
    return <LoadingScreen />;

  return (
    <>
      {showPopupNoInsurance && (
        <Popup
          showBtnClose={false}
          enableClose={false}
          open={showPopupNoInsurance}
          onClose={() => setShowPopupNoInsurance(false)}
          btnElement={(onClick) => <></>}
        >
          <NoInsuranceContent
            setOpen={setShowPopupNoInsurance}
            setSelectedInsurance={setSelectedInsurance}
            onNextStep={handleNextStep}
          />
        </Popup>
      )}
      <div className="tw-container tw-mx-auto">
        <div
          className={`${
            mode === "vacanceole"
              ? "tw-bg-primary-500"
              : "tw-bg_corseole-secondary"
          } tw-text-white tw-pb-4 tw-pt-1 tw-px-8 md:tw-p-8 tw-flex tw-flex-col md:tw-flex-row tw-items-center`}
        >
          {/* STEPPER HEADER NAVIGATION */}
          <a href={getCustomerUrlByWebUser(getWebUser(), mode)}>
            <img
              className="tw-w-[100px] md:tw-w-[170px] md:tw-mr-24 tw-mb-2 md:tw-mb-0 tw-self-start md:tw-self-center"
              src={getAbsoluteImagePath(`/assets/img/customer/${logo}`)}
            ></img>
          </a>
          <Stepper
            className="v_step tw-flex tw-flex-wrap tw-gap-y-2 tw-w-[95%] md:tw-w-[60%] tw-pb-4 md:tw-pb-0"
            activeStep={activeStep}
          >
            {steps.map((step, index) => {
              return (
                <Step
                  key={`${step.label}_${index}`}
                  classes={{ root: !step.show ? "patchMuiStep" : "" }}
                  onClick={() => {
                    step.show && activeStep > index && setActiveStep(index);
                  }}
                  className="tw-cursor-pointer"
                >
                  <StepLabel
                    StepIconProps={{
                      sx: { display: step.show ? "block" : "none" },
                      classes: {
                        active: "activeMuiStep",
                        completed:
                          mode === "vacanceole"
                            ? "fill-primary-500-important"
                            : "fill-primary_corseole-500-important",
                        root: `rootMuiStep`,
                      },
                    }}
                  >
                    <span className="tw-text-xs sm:tw-text-sm md:tw-text-base">
                      {step.show ? step.label : ""}
                    </span>
                  </StepLabel>
                </Step>
              );
            })}
          </Stepper>
        </div>
        {cart && !isEmpty(cart.reservations) ? (
          valid ? (
            <>
              <div className="tw-my-8">
                {activeErrorsSteps && (
                  <div className="tw-mb-8">
                    <Alert
                      variant={activeErrorsSteps.variant}
                      dismissable={false}
                    >
                      {activeErrorsSteps.children}
                    </Alert>
                  </div>
                )}
                <div className="tw-grid tw-grid-cols-12 tw-gap-4 tw-items-start">
                  <div className="tw-block md:tw-hidden tw-col-span-12">
                    <Popup
                      btnElement={(onClick) => (
                        <div
                          className={`tw-border ${
                            mode === "vacanceole"
                              ? "tw-border-v_blue-dark"
                              : "tw-border-primary_corseole-500"
                          } tw-p-2`}
                        >
                          <div className="tw-flex tw-items-center">
                            <div className="tw-flex-auto tw-flex tw-flex-col tw-gap-3">
                              <div>
                                <div className="tw-flex tw-items-start">
                                  <div
                                    className="tw-cursor-pointer tw-flex-auto"
                                    onClick={onClick}
                                  >
                                    {cart?.reservations![0]?.property?.label}
                                  </div>
                                </div>
                                <div
                                  onClick={onClick}
                                  className="tw-cursor-pointer tw-font-bold"
                                >
                                  {
                                    cart?.reservations![0]?.property?.address
                                      ?.city
                                  }
                                </div>
                              </div>
                              <div
                                className="tw-cursor-pointer"
                                onClick={onClick}
                              >
                                <div className="tw-text-sm tw-font-semibold">
                                  {getReservationRoomsInfosName(
                                    cart?.reservations![0]!!
                                  )}
                                </div>
                                <div className="tw-text-sm">
                                  {dateFormat(
                                    new Date(
                                      cart?.reservations![0]!!.startDate
                                    ),
                                    "dd/mm/yy"
                                  )}{" "}
                                  <span className="tw-lowercase">
                                    {getLanguageVarValue(
                                      "LANG_neobooking_to_date"
                                    )}
                                  </span>{" "}
                                  {dateFormat(
                                    new Date(cart?.reservations![0]!!.endDate),
                                    "dd/mm/yy"
                                  )}{" "}
                                  - {cart?.reservations![0]!!.nbDays}{" "}
                                  <span className="tw-lowercase">
                                    {getLanguageVarValue(
                                      "LANG_neobooking_nights"
                                    )}
                                  </span>
                                </div>
                                <div className="tw-inline">
                                  <button
                                    onClick={onClick}
                                    className={`tw-italic tw-font-bold tw-font-dosis focus:tw-outline-none tw-text-sm ${
                                      mode === "vacanceole"
                                        ? "tw-text-red-500 hover:tw-text-red-400"
                                        : "tw-text_corseole-secondary"
                                    }`}
                                  >
                                    {getLanguageVarValue(
                                      "LANG_neobooking_see_more"
                                    )}
                                  </button>
                                </div>
                              </div>
                            </div>
                            <div className="tw-cursor-pointer tw-flex-none tw-font-bold tw-flex tw-flex-col">
                              <PopupEdit
                                btnElement={(onClick) => (
                                  <button
                                    onClick={onClick}
                                    className={`tw-flex-none ${
                                      mode === "vacanceole"
                                        ? "tw-text-red-500 hover:tw-text-red-400"
                                        : "tw-text_corseole-secondary"
                                    } tw-italic tw-text-sm tw-pl-2 focus:tw-outline-none`}
                                  >
                                    +{" "}
                                    {getLanguageVarValue(
                                      "LANG_neobooking_edit"
                                    )}
                                  </button>
                                )}
                              />
                              <div onClick={onClick}>
                                {cart?.reservations![0]?.totalAmount?.amount?.toFixed(
                                  2
                                )}
                                {getCurrencySymbol(
                                  cart?.reservations![0]?.totalAmount
                                    ?.currencyCode!!
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    >
                      <BasketSummary
                        reservations={cart?.reservations!!}
                        enableEdit={false}
                        selectedInsurance={selectedInsurance}
                        setSelectedInsurance={setSelectedInsurance}
                      />
                    </Popup>
                  </div>
                  <div className="tw-col-span-12 md:tw-col-span-7 lg:tw-col-span-8">
                    <>
                      {!isFromFastCheckinOrCheckout ? (
                        <>
                          {activeStep === StepsEnum.AUTH ? (
                            <>
                              <GTMReservationPageStep1 cart={cart} />
                              <StepAuth
                                showContinueWithoutLogin={
                                  hasPresta || hasPrestaStay
                                }
                                onNextStep={onNextStep}
                              />
                            </>
                          ) : activeStep === StepsEnum.ACTIVITIES ? (
                            <>
                              <GTMReservationPageStep2 cart={cart} />
                              <div className="tw-mb-4">
                                <StepTitle
                                  subtitle={getLanguageVarValue(
                                    "LANG_neobooking_custom_stay"
                                  )}
                                />
                              </div>
                              <StepPrestation />
                            </>
                          ) : activeStep === StepsEnum.OCCUPANT ? (
                            <>
                              <GTMReservationPageStep3 cart={cart} />
                              <StepReservationContent />
                            </>
                          ) : (
                            activeStep === StepsEnum.PAYMENT && (
                              <>
                                {!user ? (
                                  <div className="tw-mb-4">
                                    <StepAuth
                                      showContinueWithoutLogin={false}
                                      onNextStep={() => undefined}
                                    />
                                  </div>
                                ) : (
                                  <>
                                    <GTMReservationPageStep4 cart={cart} />
                                    <StepPayment
                                      termsChecked={termsChecked}
                                      setTermsChecked={setTermsChecked}
                                      termsError={termsError}
                                      setTermsError={setTermsError}
                                      paymentDueTypeSelected={
                                        selectedPaymentDueType
                                      }
                                      setPaymentDueTypeSelected={
                                        setSelectedPaymentDueType
                                      }
                                      selectedPaymentMethod={
                                        selectedPaymentMethod
                                      }
                                      setSelectedPaymentMethod={
                                        setSelectedPaymentMethod
                                      }
                                      selectedPaymentOption={
                                        selectedPaymentOption
                                      }
                                      setSelectedPaymentOption={
                                        setSelectedPaymentOption
                                      }
                                      displayInsurance={showInsurance}
                                      showDiscounts={authMode !== 2}
                                      selectedInsurance={selectedInsurance}
                                      setSelectedInsurance={
                                        setSelectedInsurance
                                      }
                                    />
                                  </>
                                )}
                              </>
                            )
                          )}
                          {/* fast checking steps */}
                        </>
                      ) : (
                        <>
                          {activeStep === 0 ? (
                            <>
                              <StepReservationContent />
                            </>
                          ) : activeStep === 1 ? (
                            <>
                              <StepFastcheckout
                                selected={selectedFastcheckout}
                                setSelected={setSelectedFastcheckout}
                                singleConfirmFastCheckout={false}
                              />
                            </>
                          ) : (
                            activeStep === 2 && (
                              <StepPayment
                                termsChecked={termsChecked}
                                setTermsChecked={setTermsChecked}
                                termsError={termsError}
                                displayInsurance={false}
                                setTermsError={setTermsError}
                                paymentDueTypeSelected={selectedPaymentDueType}
                                setPaymentDueTypeSelected={
                                  setSelectedPaymentDueType
                                }
                                selectedPaymentMethod={selectedPaymentMethod}
                                setSelectedPaymentMethod={
                                  setSelectedPaymentMethod
                                }
                                selectedPaymentOption={selectedPaymentOption}
                                setSelectedPaymentOption={
                                  setSelectedPaymentOption
                                }
                                selectedInsurance={selectedInsurance}
                                setSelectedInsurance={setSelectedInsurance}
                              />
                            )
                          )}
                        </>
                      )}
                    </>

                    <div className="tw-mt-4">
                      {/* STEPPER FOOTER ACTION BACK/NEXT */}
                      {cart &&
                        !isEmpty(cart.reservations) &&
                        (!isFromFastCheckinOrCheckout ? (
                          <div className="tw-text-right tw-flex tw-flex-row-reverse tw-gap-x-2 tw-justify-center md:tw-justify-start">
                            {(!user && activeStep === StepsEnum.AUTH) ||
                            (!user && activeStep === StepsEnum.PAYMENT) ||
                            (user &&
                              activeStep === StepsEnum.AUTH &&
                              authMode === 2 &&
                              !isValidPartner()) ? (
                              <></>
                            ) : (
                              <Button
                                type="button"
                                shape="pills"
                                variant="primary"
                                additionalClassname="tw-flex tw-items-center tw-gap-x-2 tw-font-extrabold tw-capitalize"
                                onClick={() => handleNextStep(true)}
                              >
                                {getLanguageVarValue("LANG_neobooking_next")}
                                <ArrowCircleRight />
                              </Button>
                            )}
                            {activeStep === StepsEnum.AUTH ? (
                              <ButtonOriginUrl
                                text={getLanguageVarValue(
                                  "LANG_neobooking_back"
                                )}
                              />
                            ) : (
                              <>
                                {authMode === 1 &&
                                user &&
                                !hasPresta &&
                                !hasPrestaStay ? (
                                  <></>
                                ) : (
                                  <Button
                                    type="button"
                                    shape="pills"
                                    variant="light"
                                    additionalClassname="tw-flex tw-items-center tw-gap-x-2 tw-font-extrabold tw-font-montserrat tw-capitalize"
                                    onClick={handlePreviousStep}
                                  >
                                    <ArrowCircleLeft />
                                    {getLanguageVarValue(
                                      "LANG_neobooking_back_previous_step"
                                    )}
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        ) : (
                          <></>
                        ))}
                    </div>
                  </div>
                  <div
                    className={`tw-hidden md:tw-block md:tw-col-span-5 lg:tw-col-span-4 tw-sticky tw-top-20 tw-bg-white tw-border ${
                      mode === "vacanceole"
                        ? "tw-border-v_blue-dark"
                        : "tw-border-primary_corseole-600"
                    }`}
                  >
                    <BasketSummary
                      border={false}
                      enableEdit={true}
                      reservations={cart?.reservations!!}
                      title={getLanguageVarValue("LANG_neobooking_my_cart")}
                      selectedInsurance={selectedInsurance}
                      setSelectedInsurance={setSelectedInsurance}
                    />
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="tw-flex tw-justify-center tw-items-center tw-flex-col tw-gap-4 tw-w-full">
              <div className="tw-border tw-p-12 tw-text-center tw-w-full">
                <p className="tw-mb-6">
                  {getLanguageVarValue("LANG_neobooking_error_choice")}
                </p>
                <ButtonOriginUrl
                  text={getLanguageVarValue("LANG_neobooking_back_to_choice")}
                />
              </div>
            </div>
          )
        ) : (
          <div className="tw-flex tw-justify-center tw-items-center tw-flex-col tw-gap-4 tw-w-full">
            <div className="tw-border tw-p-12 tw-text-center tw-w-full">
              <p className="tw-mb-6">
                {getLanguageVarValue("LANG_neobooking_no_dispo_message")}
              </p>
              <LinkButton
                href={window.location.origin}
                additionalClassname="tw-max-w-md tw-mx-auto tw-uppercase"
              >
                {getLanguageVarValue("LANG_neobooking_new_search")}
              </LinkButton>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
const languagesVarsCodesPrestation = [
  "LANG_neobooking_no_service_available", //Aucun service disponible
  "LANG_neobooking_custom_stay", //Personnaliser votre séjour
  "LANG_neobooking_custom_dossier", //Personnaliser votre dossier
];

type StepPrestationProps = {
  showTitle?: boolean;
};
export const StepPrestation: React.FC<StepPrestationProps> = ({
  showTitle,
}) => {
  /* Hooks */
  const { getLanguageVarValue } = useSession();
  const { cart } = useCart();
  useScrollTop();

  /* Render */
  return (
    <div className="">
      {cart?.reservations?.map((x, i) => {
        return (
          <React.Fragment key={`cart_${x?.cartItemID}_${i}`}>
            <ProductList
              excludeCategories={["ASSUR"]}
              mode="global"
              showCriteria="PRESTATIONS"
              cartItemID={x?.cartItemID!!}
              emptyMessage={getLanguageVarValue(
                "LANG_neobooking_no_service_available"
              )}
              title={
                showTitle && showTitle ? (
                  <div className="tw-titre-principal-dosis">
                    {getLanguageVarValue("LANG_neobooking_custom_dossier")}
                  </div>
                ) : undefined
              }
            />
            {x?.stays?.map((stay, stayIndex) => {
              return (
                <React.Fragment key={`cart_${x?.cartItemID}_${i}`}>
                  <ProductList
                    loadingChildren={<LoadingProduct />}
                    excludeCategories={["ASSUR"]}
                    mode="stay"
                    title={
                      showTitle && showTitle ? (
                        <>
                          {!isEmpty(x.stays) && x.stays?.length!! > 1 ? (
                            <div className="tw-titre-principal-spirit">
                              {!isEmpty(stay?.distribution?.roomTypes) &&
                                stay?.distribution?.roomTypes?.[0]?.label}
                            </div>
                          ) : (
                            <div>
                              <div className="tw-titre-principal-carrot">
                                Wahou
                              </div>
                              <div className="tw-titre-principal-spirit">
                                {getLanguageVarValue(
                                  "LANG_neobooking_custom_stay"
                                )}
                              </div>
                            </div>
                          )}
                        </>
                      ) : undefined
                    }
                    key={`cart_${x?.cartItemID}_${i}_stay_${stayIndex}`}
                    cartItemID={x?.cartItemID!!}
                    stayGuiid={stay?.guiid!!}
                    emptyMessage={getLanguageVarValue(
                      "LANG_neobooking_no_service_available"
                    )}
                    showCriteria="PRESTATIONS"
                  />
                </React.Fragment>
              );
            })}
          </React.Fragment>
        );
      })}
    </div>
  );
};
const LoadingProduct = () => {
  const { mode: clientMode } = useConfig();
  const mode = clientMode ? clientMode : "vacanceole";

  return (
    <div className="tw-flex tw-flex-col tw-justify-center tw-items-center tw-gap-4">
      <div role="status">
        <svg
          className={`tw-inline tw-mr-2 tw-w-10 tw-h-10 tw-text-gray-200 tw-animate-spin ${
            mode === "vacanceole"
              ? "tw-fill-v_blue-dark"
              : "tw-fill-primary_corseole-500"
          }`}
          viewBox="0 0 100 101"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
            fill="currentColor"
          />
          <path
            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
            fill="currentFill"
          />
        </svg>
        <span className="tw-sr-only">Loading...</span>
      </div>
    </div>
  );
};
type NoInsuranceContentProps = {
  setSelectedInsurance: (insurance: string | undefined) => void;
  setOpen: (open: boolean) => void;
  onNextStep: () => void;
};
const NoInsuranceContent: React.FC<NoInsuranceContentProps> = ({
  setSelectedInsurance,
  setOpen,
  onNextStep,
}) => {
  /* Hooks */
  const client = useApolloClient();
  const { getLanguageVarValue, sessionId } = useSession();

  /* States */
  const [insuranceProducts, setInsuranceProducts] = useState<
    ProductByCategory[]
  >([]);

  /* Functions */
  const handleSetNoInsurance = () => {
    onNextStep();
    setOpen(false);
  };

  const handleDoChoice = () => {
    setOpen(false);
    scrollTop();
  };

  useEffect(() => {
    client
      .query<Query, QueryGetBookingProductsArgs>({
        query: getBookingProductsQuery(),
        variables: {
          session: { name: sessionId },
          input: {
            categoryCodes: ["ASSUR"],
          },
        },
        fetchPolicy: "no-cache",
      })
      .then((res) => {
        if (res.data.getBookingProducts?.bookingProducts) {
          setInsuranceProducts(
            res.data.getBookingProducts?.bookingProducts as ProductByCategory[]
          );
        }
      });
  }, []);

  return (
    <div className="tw-px-8">
      <h3 className="tw-titre-principal-dosis tw-text-center tw-font-bold">
        {getLanguageVarValue("LANG_neobooking_insurance_holiday")}
      </h3>
      <p className="tw-text-orange-500 tw-text-center tw-my-4">
        {getLanguageVarValue("LANG_neobooking_select_insurance")}
      </p>
      <div className="shadow-custom tw-p-4">
        <p className="tw-mb-4">
          {getLanguageVarValue("LANG_neobooking_info_insurance")}
        </p>
        <ul>
          <li className="tw-flex tw-mb-2">
            <img
              className="tw-w-6 tw-inline-block tw-mr-2"
              src={getAbsoluteImagePath("/assets/img/icons/validation.svg")}
            ></img>
            <span>
              {getLanguageVarValue(
                "LANG_neobooking_insurance_cancellation_stay"
              )}
            </span>
          </li>
          <li className="tw-flex tw-mb-2">
            <img
              className="tw-w-6 tw-inline-block tw-mr-2"
              src={getAbsoluteImagePath("/assets/img/icons/validation.svg")}
            ></img>
            <span>
              {getLanguageVarValue(
                "LANG_neobooking_insurance_changes_vacation_dates"
              )}
            </span>
          </li>
          <li className="tw-flex tw-mb-2">
            <img
              className="tw-w-6 tw-inline-block tw-mr-2"
              src={getAbsoluteImagePath("/assets/img/icons/validation.svg")}
            ></img>
            <span>
              {getLanguageVarValue("LANG_neobooking_insurance_job_transfer")}
            </span>
          </li>
          <li className="tw-flex tw-mb-2">
            <img
              className="tw-w-6 tw-inline-block tw-mr-2"
              src={getAbsoluteImagePath("/assets/img/icons/validation.svg")}
            ></img>
            <span>
              {getLanguageVarValue("LANG_neobooking_insurance_covid")}
            </span>
          </li>
        </ul>
      </div>
      <Button
        type="button"
        variant="gradient"
        onClick={handleDoChoice}
        additionalClassname="tw-my-4 tw-ml-auto tw-block !tw-w-full"
      >
        {getLanguageVarValue("LANG_neobooking_make_my_choice")}
      </Button>
      <div className="tw-text-center">
        <button
          onClick={handleSetNoInsurance}
          className="tw-underline tw-font-dosis tw-font-semibold tw-text-xl tw-underline-offset-[6px]"
        >
          {getLanguageVarValue("LANG_neobooking_continue_without_insurance")}
        </button>
      </div>
    </div>
  );
};

type ButtonOriginUrlProps = {
  text: string;
};
const ButtonOriginUrl: React.FC<ButtonOriginUrlProps> = ({ text }) => {
  /* Hooks */
  const { clear } = useCart();
  const [searchParams, setSearchParams] = useSearchParams();
  const origin_url = searchParams.get("originUrl");
  const handleRedirect = () => {
    trackClick(
      "Tunnel - Panier",
      "click bouton",
      "Bouton - Réserver un autre séjour",
      ""
    );
    //on remove la proposal key et on clear le cart au retour sur choix hébergement
    clear((err) => {
      if (!err) {
        removeLocalStorageItem("rsl_proposal_key");
        window.location.href = origin_url!!;
      }
    }, false);
  };
  return origin_url ? (
    <Button
      type="button"
      shape="pills"
      variant="light"
      additionalClassname="tw-flex tw-items-center tw-gap-x-2 tw-font-extrabold tw-capitalize"
      onClick={handleRedirect}
    >
      <ArrowCircleLeft />
      {text}
    </Button>
  ) : (
    <></>
  );
};
export default ReservationPage;
